import React from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useFieldArray,
} from "react-hook-form";
import { LuAlertCircle, LuFileText, LuPlus, LuTrash2 } from "react-icons/lu";

import Input from "../ui/Input";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  arrayAddButtonClass,
  arrayRemoveButtonClass,
  arrayRowClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formFieldIconClass,
} from "../ui/styles";
import { hasRate } from "../../static/processRate";
import { IMaterialLinesFormInput } from "../../types/materialLine";

const costIndexClass =
  "md:hidden flex items-center justify-between gap-2 text-sm font-semibold text-[#113F67]";

type Props = {
  control: Control<IMaterialLinesFormInput>;
  register: UseFormRegister<IMaterialLinesFormInput>;
  errors: FieldErrors<IMaterialLinesFormInput>;
};

// A purchase's additional costs (વધારાના ખર્ચ), a note and an amount each, as the user's reference app's dies purchase
// form has them. The amount box is a rate box, so it keeps up to 2 decimals like the lines' prices; 0 is an amount.
const AdditionalCostForm = ({ control, register, errors }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "additional_costs",
  });

  return (
    <div className="space-y-4">
      <label className={fieldLabelClass}>વધારાના ખર્ચ</label>

      {fields.map((field, index) => {
        const noteError = errors?.additional_costs?.[index]?.note;
        const amountError = errors?.additional_costs?.[index]?.amount;

        const removeButton = (
          <button
            type="button"
            title="કાઢી નાખો"
            className={arrayRemoveButtonClass}
            onClick={() => remove(index)}
          >
            <LuTrash2 className="h-5 w-5" />
          </button>
        );

        return (
          <div key={field.id} className={arrayRowClass}>
            <div className={costIndexClass}>
              <span>ખર્ચ - {index + 1}</span>
              {removeButton}
            </div>

            <div className="md:col-span-7 space-y-2">
              <label
                htmlFor={`additional-cost-note-${index}`}
                className={fieldLabelClass}
              >
                નોંધ
              </label>
              <div className={cx("relative", noteError && "error")}>
                <LuFileText className={formFieldIconClass} />
                <Input
                  id={`additional-cost-note-${index}`}
                  placeholder="ખર્ચની નોંધ દાખલ કરો"
                  maxLength={255}
                  className={cx(fieldInputClass(!!noteError), "bg-white")}
                  {...register(`additional_costs.${index}.note`, {
                    validate: (value) => (value || "").trim() !== "",
                  })}
                />
                {noteError && <LuAlertCircle className={fieldErrorIconClass} />}
              </div>
              {noteError && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  નોંધ દાખલ કરો
                </p>
              )}
            </div>

            <div className="md:col-span-4 space-y-2">
              <label
                htmlFor={`additional-cost-amount-${index}`}
                className={fieldLabelClass}
              >
                રકમ
              </label>
              <Controller
                control={control}
                name={`additional_costs.${index}.amount`}
                rules={{ validate: (value) => hasRate(value) }}
                render={({ field: amountField }) => (
                  <div className={cx("relative", amountError && "error")}>
                    <RateInput
                      ref={amountField.ref}
                      id={`additional-cost-amount-${index}`}
                      name={amountField.name}
                      value={amountField.value}
                      placeholder="રકમ દાખલ કરો"
                      hasError={!!amountError}
                      onChange={amountField.onChange}
                      onBlur={amountField.onBlur}
                    />
                    {amountError && (
                      <LuAlertCircle className={fieldErrorIconClass} />
                    )}
                  </div>
                )}
              />
              {amountError && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  રકમ દાખલ કરો
                </p>
              )}
            </div>

            <div className="hidden md:col-span-1 md:flex md:items-end md:justify-center">
              {removeButton}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className={arrayAddButtonClass}
        onClick={() => append({ note: "", amount: "" })}
      >
        <LuPlus className="h-4 w-4" />
        વધારાનો ખર્ચ ઉમેરો
      </button>
    </div>
  );
};

export default AdditionalCostForm;
